import moment from 'moment'

import {
  ApplicationLabelTypes,
  type ApplicationLabel,
  type ExperimentEffect,
  type ExperimentHistoryEntry,
  type ExperimentRunSummary,
  type FailureFlagExperiment,
  type FailureFlagApplication,
  type FailureFlag
} from 'types/api/failure-flags/failure-flags'
import { pluralize } from 'utils/strings'
import { formatDateTime } from 'utils/time/date'

import type { OptionsType } from 'components/failure-flags/create/form-utils'
import type { Client } from 'types/api/attacks/client'

/**
 * Constants
 */
export const APPLICATION_DETAILS_LABELS_LIST_COUNT = 10
export const APPLICATION_DETAILS_EXPERIMENTS_LIST_COUNT = 10
export const FLAG_DETAILS_EXPERIMENTS_LIST_COUNT = 10
export const FLAG_DETAILS_APPLICATIONS_LIST_COUNT = 10
export const JSON_SELECTOR_HELP_TEXT = 'Optional { [key: string]: string[] }'
export const JSON_EFFECT_HELP_TEXT = 'Required'

const timeUnitLookup = {
  d: 'day',
  h: 'hour',
  m: 'minute'
}

/**
 * Utility Functions
 */
export function isFailureFlagsApplication(
  app: Client | FailureFlagApplication
): app is FailureFlagApplication {
  return 'name' in app && 'experiments' in app
}

export function formatApplicationLabels(
  key: string,
  values?: ApplicationLabel[]
) {
  if (!values) {
    return ''
  }

  if (key === ApplicationLabelTypes.Region) {
    return values
      .map(({ value, cardinality }) => `${value} (${cardinality})`)
      .join('; ')
  }

  if (Array.isArray(values)) {
    return values
      .map(({ value, cardinality }) => `${value} (${cardinality})`)
      .join('; ')
  }

  if (typeof values === 'string') {
    return values
  }

  // TODO we need proper translations for differing values here
  return JSON.stringify(values)
}

export function getLastRunFromRecentHistory(
  recentHistory: ExperimentHistoryEntry[] = []
): string {
  if (recentHistory.length === 0) {
    return ''
  }

  const lastRun = Math.max(
    ...recentHistory.map((run) => new Date(run.startTime).getTime())
  )

  return new Date(lastRun).toISOString()
}

export function isExperimentRunning(experiment?: FailureFlagExperiment) {
  return Boolean(experiment?.currentRun?.startTime)
}

export function safelyParseJSONString(str: string): { [k: string]: string[] } {
  try {
    return JSON.parse(str)
  } catch (e) {
    return {}
  }
}

export function isValidJSON(value: ExperimentEffect) {
  try {
    JSON.stringify(value)
    return true
  } catch (e) {
    return false
  }
}

/**
 * Return the link to the failure flag experiment run details page
 */
export function getFailureFlagDetailsLink(
  failureFlag: FailureFlagExperiment | ExperimentRunSummary
) {
  return {
    to: `/failure-flags/experiment/${failureFlag.id}`,
    state: { failureFlag }
  }
}

export const effectKeyOptions: OptionsType[] = [
  {
    label: 'latency',
    id: 'latency'
  },
  {
    label: 'exception',
    id: 'exception'
  },
  {
    label: 'httpResponse',
    id: 'httpResponse'
  }
]

export const EFFECT_FIELDS = {
  latency: {
    ms: 0,
    jitter: 0
  },
  exception: {
    message: ''
  },
  httpResponse: {
    code: 500,
    contentType: '',
    body: ''
  },
  crash: {
    code: 0,
    message: ''
  }
}

// For these flag names, ONLY these effects are available (replaces the standard list).
const FLAG_EXCLUSIVE_EFFECTS: Record<string, OptionsType[]> = {
  process: [{ label: 'crash', id: 'crash' }]
}

export function getEffectKeyOptions(flagName?: string): OptionsType[] {
  if (flagName && Object.hasOwn(FLAG_EXCLUSIVE_EFFECTS, flagName)) {
    return FLAG_EXCLUSIVE_EFFECTS[flagName]
  }
  return effectKeyOptions
}

// A flag with 'scheme' is a network flag; one that also has 'path' is HTTP.
// HTTPS flags lack 'path', making httpResponse ineffective (traffic is encrypted).
export function isHttpsFlag(flag: FailureFlag): boolean {
  return flag.labelKeys.includes('scheme') && !flag.labelKeys.includes('path')
}

// Fields within an effect type whose values must be stored as base64.
// Key = effect type name, value = list of field names.
export const EFFECT_FIELDS_BASE64: Record<string, string[]> = {
  httpResponse: ['body']
}

export function isBase64Field(
  parentsKey: string | undefined,
  currentKey: string
): boolean {
  return Boolean(
    parentsKey && EFFECT_FIELDS_BASE64[parentsKey]?.includes(currentKey)
  )
}

export function safeBase64Encode(value: string): string {
  try {
    return btoa(value)
  } catch {
    return value
  }
}

export function safeBase64Decode(value: string): string {
  try {
    return atob(value)
  } catch {
    return value
  }
}

export function getMomentUnitsFromFailureFlagExperimentDuration(
  duration: string
) {
  const value = parseInt(duration.slice(0, -1), 10)
  const isValidValue = !Number.isNaN(value)
  const finalChar = duration.at(-1) ?? ''
  const timeUnit = timeUnitLookup[finalChar]

  if (timeUnit && isValidValue) {
    return [value, pluralize(timeUnit, value)]
  }
}

export function formatFailureFlagExperimentDuration(duration: string) {
  const momentUnits = getMomentUnitsFromFailureFlagExperimentDuration(duration)

  return momentUnits ? momentUnits.join(' ') : 'n/a'
}

export function getEndTimeFromCurrentRun(startTime: string, duration: string) {
  const addition = getMomentUnitsFromFailureFlagExperimentDuration(
    duration
  ) ?? [0, 'minutes']
  return formatDateTime(moment(startTime).add(...addition))
}
